/**
 * What the `{{…}}` dropdown offers.
 *
 * It used to offer the built-in `$` variables and nothing else, which is
 * backwards: the variables a user reaches for most are the ones they saved
 * themselves, and those were the only ones you had to type from memory. Saved
 * variables now come FIRST and the generated `$` ones sit below them (owner,
 * 22.09.2026).
 *
 * Kept out of the component because "which variables exist, in what order" is a
 * data question with real edge cases — shadowing across scopes, secrets, the
 * row you are editing right now — and none of it needs a DOM to test.
 */
import { DYNAMIC_VARIABLES, DYNAMIC_VARIABLE_DOCS, filterDynamicVariableNames } from './dynamicVariables';
import { displayValueOf } from './variableDisplay';
import { SCOPE_PRECEDENCE } from './variablePrecedence';

/**
 * Scope precedence, narrowest first — the same order `getVariableValue` uses to
 * resolve a name. A key defined in two scopes is ONE suggestion, labelled with
 * the scope that would actually win; offering it twice would suggest the user
 * has a choice they do not have.
 */
const SCOPE_ORDER = SCOPE_PRECEDENCE.map((scope) => [`${scope}Variables`, scope]);

/** A secret's VALUE is never put in a dropdown; the name is still offered. */
const previewOf = displayValueOf;

/**
 * Every usable saved variable, resolved and de-duplicated.
 *
 * @param {object} scopes {folderVariables, collectionVariables, environmentVariables, workspaceVariables, globalVariables}
 * @returns {Array<{name: string, scope: string, preview: string}>}
 */
export function savedVariables(scopes = {}) {
  const seen = new Set();
  const out = [];
  for (const [key, scopeName] of SCOPE_ORDER) {
    const list = scopes[key];
    if (!Array.isArray(list)) continue;
    for (const variable of list) {
      const name = variable?.key;
      // A disabled row is not in play, and the trailing blank row of every
      // editor would otherwise show up as an empty entry.
      if (!name || variable.enabled === false || seen.has(name)) continue;
      seen.add(name);
      out.push({ name, scope: scopeName, preview: previewOf(variable) });
    }
  }
  return out;
}

/** Case-insensitive "starts with", falling back to "contains" for a late match. */
function matches(name, prefix) {
  if (!prefix) return true;
  return name.toLowerCase().includes(prefix.toLowerCase());
}

/**
 * Which halves of the list a field wants. Not every field wants both:
 *
 *   'all'       params, auth, anywhere a value is just a value
 *   'variables' the URL — `$randomFirstName` in a URL is nobody's intent
 *   'dynamic'   a variable's OWN value, where offering the variables of the
 *               very table you are editing invites a variable defined in terms
 *               of itself (owner, 23.09.2026)
 */
export const SUGGEST_ALL = 'all';
export const SUGGEST_VARIABLES = 'variables';
export const SUGGEST_DYNAMIC = 'dynamic';

/**
 * The dropdown's items for a typed prefix.
 *
 * @param {string} prefix     what the user has typed inside `{{`, `$` included if present
 * @param {object} scopes     see savedVariables
 * @param {string} include    one of SUGGEST_ALL | SUGGEST_VARIABLES | SUGGEST_DYNAMIC
 * @returns {Array<{name: string, kind: 'saved'|'dynamic', detail: string}>}
 */
export function suggestionsFor(prefix = '', scopes = {}, include = SUGGEST_ALL) {
  const typed = String(prefix || '');
  const wantsSaved = include !== SUGGEST_DYNAMIC;
  const wantsDynamic = include !== SUGGEST_VARIABLES;

  // An explicit `$` means the user is asking for a generated value by name —
  // listing their own variables there would be noise. A field that does not
  // offer generated values at all answers with nothing, which closes the list.
  if (typed.startsWith('$')) {
    if (!wantsDynamic) return [];
    return filterDynamicVariableNames(typed).map((name) => ({
      name,
      kind: 'dynamic',
      detail: DYNAMIC_VARIABLE_DOCS[name] || '',
    }));
  }

  const saved = !wantsSaved ? [] : savedVariables(scopes)
    .filter((v) => matches(v.name, typed))
    // Anything that starts with what was typed beats a mid-string match.
    .sort((a, b) => {
      const p = typed.toLowerCase();
      const aStarts = a.name.toLowerCase().startsWith(p);
      const bStarts = b.name.toLowerCase().startsWith(p);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return 0;
    })
    .map((v) => ({ name: v.name, kind: 'saved', detail: v.preview, scope: v.scope }));

  const dynamic = !wantsDynamic ? [] : Object.keys(DYNAMIC_VARIABLES)
    .filter((name) => matches(name, typed ? `$${typed}` : ''))
    .map((name) => ({ name, kind: 'dynamic', detail: DYNAMIC_VARIABLE_DOCS[name] || '' }));

  return [...saved, ...dynamic];
}

export default suggestionsFor;
